import React from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, Menu, LogOut, Settings, User, Crown } from 'lucide-react';
import { useAuthStore, useNotificationStore, useUIStore, useGenerationStore, useSonicStore, useOriginalsStore } from '@soundbyte/stores';
import { Avatar, Badge } from '@/components/ui';
import { Modal } from '@/components/ui/modal';
import { ROUTES, MAIN_SITE_URL } from '@soundbyte/config/constants';
import { formatDistanceToNow } from 'date-fns';
import type { Notification } from '@soundbyte/types';
import logoIcon from '@/assets/logo-icon.png';

export const Header: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { notifications, unreadCount, fetchNotifications, markAsRead, markAllAsRead } = useNotificationStore();
  const { toggleSidebar } = useUIStore();
  const { clearAll: clearGenerations } = useGenerationStore();
  const { resetWizard: resetSonicWizard } = useSonicStore();
  const { resetWizard: resetOriginalsWizard } = useOriginalsStore();

  const [showUserMenu, setShowUserMenu] = React.useState(false);
  const [showNotifications, setShowNotifications] = React.useState(false);
  const [selectedNotification, setSelectedNotification] = React.useState<Notification | null>(null);

  const notificationsRef = React.useRef<HTMLDivElement>(null);
  const userMenuRef = React.useRef<HTMLDivElement>(null);

  // Fetch notifications when dropdown opens
  React.useEffect(() => {
    if (showNotifications) {
      fetchNotifications();
    }
  }, [showNotifications, fetchNotifications]);

  // Close dropdowns when clicking or tapping outside, or pressing Escape.
  React.useEffect(() => {
    const handlePointerOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;

      if (showNotifications && notificationsRef.current && !notificationsRef.current.contains(target)) {
        setShowNotifications(false);
      }

      if (showUserMenu && userMenuRef.current && !userMenuRef.current.contains(target)) {
        setShowUserMenu(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (showNotifications) setShowNotifications(false);
        if (showUserMenu) setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handlePointerOutside);
    document.addEventListener('touchstart', handlePointerOutside, { passive: true });
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerOutside);
      document.removeEventListener('touchstart', handlePointerOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showNotifications, showUserMenu]);

  const handleLogout = async () => {
    // Clear all user-specific data to prevent data leakage between accounts.
    clearGenerations();
    resetSonicWizard();
    resetOriginalsWizard();
    try {
      await logout();
    } catch {
      // Logout may fail (network); regardless, proceed to /login so the user
      // isn't stranded with half-cleared state.
    } finally {
      navigate(ROUTES.LOGIN);
    }
  };

  const tierColors = {
    free: 'secondary',
    plus: 'default',
    vip: 'warning',
    collectors: 'warning',
  } as const;

  return (
    <>
    <header className="sticky top-0 z-40 h-16 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-full items-center justify-between px-4 lg:px-6">
        {/* Left: Menu + Logo */}
        <div className="flex items-center gap-4">
          <button
            type="button"
            aria-label="Toggle navigation menu"
            onClick={toggleSidebar}
            className="rounded-md p-2 hover:bg-muted lg:hidden"
          >
            <Menu className="h-5 w-5" aria-hidden="true" />
          </button>

          <Link to={ROUTES.DASHBOARD} className="flex items-center gap-2">
            <img src={logoIcon} alt="SoundBYTE" className="h-8 w-8" />
            <span className="hidden font-semibold text-foreground sm:block">
              Playground
            </span>
          </Link>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2">
          {/* Notifications */}
          <div className="relative" ref={notificationsRef}>
            <button
              type="button"
              aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'}
              aria-expanded={showNotifications}
              aria-haspopup="menu"
              onClick={() => {
                setShowNotifications(!showNotifications);
                setShowUserMenu(false);
              }}
              className="relative rounded-md p-2 hover:bg-muted"
            >
              <Bell className="h-5 w-5" aria-hidden="true" />
              {unreadCount > 0 && (
                <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <div className="fixed left-3 right-3 top-[4.5rem] z-[9999] sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-80 rounded-lg border border-border bg-card shadow-lg">
                  {/* Header */}
                  <div className="flex items-center justify-between border-b border-border px-4 py-3">
                    <h3 className="font-semibold text-foreground">Notifications</h3>
                    {unreadCount > 0 && (
                      <button
                        onClick={() => markAllAsRead()}
                        className="text-xs text-primary hover:underline"
                      >
                        Mark all as read
                      </button>
                    )}
                  </div>

                  {/* Notifications List */}
                  <div className="max-h-[70vh] sm:max-h-96 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center text-muted-foreground">
                        <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No notifications</p>
                      </div>
                    ) : (
                      <>
                        {notifications.slice(0, 10).map((notification) => (
                          <div
                            key={notification.id}
                            className={`flex items-start gap-3 px-4 py-3 hover:bg-muted/50 cursor-pointer border-b border-border last:border-0 ${
                              !notification.read ? 'bg-primary/5' : ''
                            }`}
                            onClick={() => {
                              if (!notification.read) {
                                markAsRead(notification.id);
                              }
                              setSelectedNotification(notification);
                              setShowNotifications(false);
                            }}
                          >
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm ${!notification.read ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                                {notification.title}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {notification.message}
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                              </p>
                            </div>
                            {!notification.read && (
                              <div className="w-2 h-2 rounded-full bg-primary mt-2 shrink-0" />
                            )}
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                </div>
            )}
          </div>

          {/* User Menu */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => {
                setShowUserMenu(!showUserMenu);
                setShowNotifications(false);
              }}
              className="flex items-center gap-2 rounded-full p-1 hover:bg-muted"
            >
              <Avatar
                src={user?.avatarUrl}
                fallback={user?.fullName || 'User'}
                size="sm"
              />
            </button>

            {showUserMenu && (
              <div className="absolute right-0 top-full z-[9999] mt-2 w-56 rounded-lg border border-border bg-card p-2 shadow-lg">
                  {/* User Info */}
                  <div className="border-b border-border px-3 py-2 mb-2">
                    <p className="font-medium text-foreground">{user?.fullName}</p>
                    <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
                    <Badge
                      variant={tierColors[user?.subscriptionTier || 'free']}
                      className="mt-2"
                    >
                      {user?.subscriptionTier === 'collectors' ? 'Collectors Circle' : user?.subscriptionTier === 'vip' ? 'Patron Circle' : user?.subscriptionTier === 'plus' ? 'BYTEclub' : 'Free'}
                    </Badge>
                  </div>

                  {/* Menu Items */}
                  <Link
                    to={ROUTES.SETTINGS}
                    onClick={() => setShowUserMenu(false)}
                    className="flex items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-muted"
                  >
                    <Settings className="h-4 w-4" />
                    Settings
                  </Link>

                  <Link
                    to={ROUTES.SUBSCRIPTION}
                    onClick={() => setShowUserMenu(false)}
                    className="flex items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-muted"
                  >
                    <Crown className="h-4 w-4" />
                    Subscription
                  </Link>

                  <a
                    href={MAIN_SITE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-muted"
                  >
                    <User className="h-4 w-4" />
                    Main Website
                  </a>

                  <hr className="my-2 border-border" />

                  <button
                    onClick={handleLogout}
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-destructive hover:bg-destructive/10"
                  >
                    <LogOut className="h-4 w-4" />
                    Logout
                  </button>
                </div>
            )}
          </div>
        </div>
      </div>
    </header>
      {/* Notification Detail Modal - portaled to body to escape header stacking context */}
      {/* Notification Detail Modal - portaled to body to escape header stacking context */}
      {createPortal(
        <Modal
          isOpen={!!selectedNotification}
          onClose={() => setSelectedNotification(null)}
          title={selectedNotification?.title}
          size="sm"
        >
          <p className="text-foreground/90">{selectedNotification?.message}</p>
          {selectedNotification?.createdAt && (
            <p className="text-xs text-muted-foreground mt-4">
              {formatDistanceToNow(new Date(selectedNotification.createdAt), { addSuffix: true })}
            </p>
          )}
        </Modal>,
        document.body
      )}
    </>
  );
};
